<script setup>
import { useAlert } from 'dashboard/composables';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LoadingIndicator from 'shared/components/LoadingIndicator.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedAssistant = ref({});
const copyingAssistantId = ref(null);

const assistantsList = computed(
  () => getters['assistants/getAssistants'].value
);
const uiFlags = computed(() => getters['assistants/getUIFlags'].value);

const deleteConfirmText = computed(
  () =>
    `${t('ASSISTANT_MGMT.DELETE.CONFIRM.YES')} ${selectedAssistant.value.name}`
);

const deleteRejectText = computed(
  () =>
    `${t('ASSISTANT_MGMT.DELETE.CONFIRM.NO')} ${selectedAssistant.value.name}`
);

const confirmDeleteMessage = computed(
  () =>
    `${t('ASSISTANT_MGMT.DELETE.CONFIRM.MESSAGE')} ${
      selectedAssistant.value.name
    }?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('ASSISTANT_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      assistantName: selectedAssistant.value.name,
    })}`
);

const deleteAssistant = async ({ id }) => {
  try {
    await store.dispatch('assistants/delete', id);
    useAlert(t('ASSISTANT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('ASSISTANT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};

const copyAssistant = async assistant => {
  try {
    copyingAssistantId.value = assistant.id;
    const response = await store.dispatch('assistants/copy', {
      id: assistant.id,
      name: `${assistant.name} Copy`,
    });
    // Show message immediately when job is queued
    if (response?.status === 'queued') {
      useAlert(t('ASSISTANT_MGMT.COPY.API.QUEUED_MESSAGE'));
    } else {
      useAlert(t('ASSISTANT_MGMT.COPY.API.SUCCESS_MESSAGE'));
    }
  } catch (error) {
    useAlert(t('ASSISTANT_MGMT.COPY.API.ERROR_MESSAGE'));
  } finally {
    copyingAssistantId.value = null;
  }
};

const openDeletePopup = assistant => {
  selectedAssistant.value = assistant;
  showDeletePopup.value = true;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedAssistant.value = {};
};

const confirmDeletion = () => {
  deleteAssistant(selectedAssistant.value);
  closeDelete();
};

onMounted(() => {
  store.dispatch('assistants/get');
});
</script>

<template>
  <SettingsLayout
    :no-records-found="!assistantsList.length"
    :no-records-message="$t('ASSISTANT_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('ASSISTANT_MGMT.HEADER')"
        :description="$t('ASSISTANT_MGMT.DESCRIPTION')"
        :link-text="$t('ASSISTANT_MGMT.LEARN_MORE')"
        feature-name="inboxes"
      >
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_assistant_new' }">
            <NextButton
              v-if="isAdmin"
              icon="i-lucide-circle-plus"
              :label="$t('ASSISTANT_MGMT.NEW')"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="assistant in assistantsList"
          :key="assistant.id"
          class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
        >
          <div class="flex items-start justify-between mb-4">
            <div class="flex h-12 w-12">
              <Thumbnail
                v-if="assistant.avatar_url"
                class="max-w-full rounded-md border border-slate-50 dark:border-slate-700/50 shadow-sm"
                :src="assistant.avatar_url"
                :username="assistant.name"
                size="48px"
              />
              <div
                v-else
                class="w-12 h-12 bg-black-50 dark:bg-black-800 rounded-full p-2 ring ring-opacity-20 dark:ring-opacity-80 ring-black-100 dark:ring-black-900 border border-slate-100 dark:border-slate-700/50 shadow-sm block"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  class="opacity-80 p-1"
                >
                  <path
                    fill="currentColor"
                    d="M1 12c0-5.185 0-7.778 1.61-9.39C4.223 1 6.816 1 12 1s7.778 0 9.39 1.61C23 4.223 23 6.816 23 12s0 7.778-1.61 9.39C19.777 23 17.184 23 12 23s-7.778 0-9.39-1.61C1 19.777 1 17.184 1 12"
                    opacity=".35"
                  />
                  <path
                    fill="currentColor"
                    d="M2.61 21.389c1.612 1.61 4.205 1.61 9.39 1.61s7.778 0 9.39-1.61c1.492-1.493 1.601-3.829 1.61-8.29h-3.476c-.996 0-1.494 0-1.931.202c-.438.201-.762.58-1.41 1.335l-.666.777c-.648.756-.972 1.134-1.41 1.335s-.935.202-1.93.202h-.353c-.996 0-1.494 0-1.931-.202c-.438-.2-.762-.579-1.41-1.335l-.666-.777c-.648-.756-.972-1.134-1.41-1.335s-.935-.201-1.93-.201H1c.008 4.46.118 6.796 1.61 8.289"
                  />
                </svg>
              </div>
            </div>
            <div class="flex">
              <button
                class="flex w-10 h-10 items-center justify-center text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-md transition-colors"
                :class="{
                  'opacity-50 cursor-not-allowed':
                    copyingAssistantId === assistant.id,
                }"
                :disabled="copyingAssistantId === assistant.id"
                :title="$t('ASSISTANT_MGMT.COPY.BUTTON_TEXT')"
                @click="copyAssistant(assistant)"
              >
                <Icon icon="i-lucide-copy" />
              </button>
              <router-link
                :to="{
                  name: 'settings_assistant_show',
                  params: { assistantId: assistant.id },
                }"
                class="flex w-10 h-10 items-center justify-center text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-md transition-colors"
                :title="$t('ASSISTANT_MGMT.EDIT.BUTTON_TEXT')"
              >
                <Icon icon="i-lucide-settings-2" />
              </router-link>
              <button
                v-if="isAdmin"
                class="flex w-10 h-10 items-center justify-center text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                :title="$t('ASSISTANT_MGMT.DELETE.BUTTON_TEXT')"
                @click="openDeletePopup(assistant)"
              >
                <Icon icon="i-lucide-trash-2" />
              </button>
            </div>
          </div>
          <div class="flex flex-col m-0 flex-1">
            <div
              class="font-medium mb-2 text-slate-800 dark:text-slate-100 flex justify-between items-center"
            >
              <span class="text-base font-semibold">{{ assistant.name }}</span>
            </div>
            <div />
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('ASSISTANT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedAssistant.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />

    <LoadingIndicator
      v-if="copyingAssistantId !== null"
      position="fixed"
      placement="bottom-right"
      message="Copying assistant..."
      icon="spinner"
      size="md"
    />
  </SettingsLayout>
</template>
